import { ModalConfirmation, ModalConfirmationProps, notify, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../config/queryClient'
import { QueryKeys } from '../../../../enums/queryKeys'
import { useSegment } from '../../../../hooks'
import { APIError } from '../../../../utils'
import { getErrorMessage } from '../../../../utils/getErrorMessage'
import { BillState } from '../../enums/billState'
import { useGetBill } from '../../hooks/useGetBill'
import { useUpdateBill } from '../../hooks/useUpdateBill'
import { getBillTransactionsQueries } from '../../utils/getBillTransactionsQueries'
import { BillVoidConfirmationMessage } from './elements/BillVoidConfirmationMessage'

interface VoidBillConfirmationModalProps extends ModalConfirmationProps {
  billId: string
  onSuccess?: () => void
}

export const VoidBillConfirmationModal = ({
  billId,
  id,
  onSuccess,
  ...props
}: VoidBillConfirmationModalProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { track } = useSegment()
  const { close: closeVoidConfirmationModal } = useModal(id)
  const { data: billData } = useGetBill(billId)

  const bill = billData?.bill
  const creditedBillId = bill?.creditedBillId

  const { update: updateBill, isLoading: isBillUpdating } = useUpdateBill({
    onSuccess: () => {
      track('Bill Voided (Client)')

      const { queryKey: billTransactionsQueryKey } = getBillTransactionsQueries(billId)
      queryClient.setQueryData([QueryKeys.Bill, billId], {
        billData,
        bill: { ...(billData?.bill || {}), state: BillState.Voided },
      })

      if (creditedBillId) {
        queryClient.invalidateQueries([QueryKeys.Bill, creditedBillId])
      }
      queryClient.invalidateQueries([QueryKeys.Bill, billId])
      queryClient.invalidateQueries([QueryKeys.Bills])
      queryClient.invalidateQueries(billTransactionsQueryKey)
      queryClient.invalidateQueries([QueryKeys.Transactions])
      closeVoidConfirmationModal()
      onSuccess?.()
    },
    onError: (error: APIError | undefined) => {
      const message = getErrorMessage(error, 'bill') || 'Could not void the bill'
      notify({ id: 'void-bill-error', message, variant: 'error' })
    },
  })

  const handleVoidModalOkClick = useCallback(() => {
    if (!organization?.id) {
      return
    }

    updateBill({ billId, payload: { state: BillState.Voided, organizationId: organization.id } })
    // eslint-disable-next-line @typescript-eslint/naming-convention
    track('Bill Payment Voided (Client)', { bill_id: billId })
  }, [billId, organization?.id, updateBill, track])

  return (
    <ModalConfirmation
      {...props}
      id={id}
      cancelLabel={t('no')}
      danger
      message={<BillVoidConfirmationMessage balanceModifiers={bill?.balanceModifiers} />}
      okLabel={t('bill.void_modal.ok')}
      okLoading={isBillUpdating}
      onOk={handleVoidModalOkClick}
      title={t('bill.void_modal.title')}
    />
  )
}
